import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Copy, CheckCircle2, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useNavigate } from "react-router-dom";

const SUPABASE_URL = "https://nbhzbstabtkjrhrwfhoj.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5iaHpic3RhYnRranJocndmaG9qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM3NjcwMjUsImV4cCI6MjA3OTM0MzAyNX0.iJ4w5kRK-jv0n40HxGStEIgVkj0U5pzqzfEpR0ZPOq4";

interface PixData {
  qr_code: string;
  qr_code_base64: string;
  ticket_url: string;
}

export function PixPayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState<PixData | null>(null);
  const [paymentId, setPaymentId] = useState<number | null>(null);
  const [status, setStatus] = useState<string>("idle");
  const [copied, setCopied] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function createPixPayment() {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão expirada. Faça login novamente.");
        navigate("/login");
        return;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ payment_type: "pix" }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Erro ao criar pagamento PIX");
      }

      setPixData(data.pix);
      setPaymentId(data.payment_id);
      setStatus("pending");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Polling for payment status
  useEffect(() => {
    if (status !== "pending" || !paymentId) return;

    pollingRef.current = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const res = await fetch(`${SUPABASE_URL}/functions/v1/check-payment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ payment_id: paymentId }),
        });

        const data = await res.json();

        if (data.status === "approved") {
          setStatus("approved");
          if (pollingRef.current) clearInterval(pollingRef.current);
          toast.success("Pagamento aprovado! 🎉");
          setTimeout(() => navigate("/userhome?payment=success"), 2000);
        } else if (data.status === "rejected" || data.status === "cancelled") {
          setStatus("rejected");
          if (pollingRef.current) clearInterval(pollingRef.current);
          toast.error("Pagamento rejeitado. Tente novamente.");
        }
      } catch {
        // silent retry
      }
    }, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [status, paymentId, navigate]);

  async function copyCode() {
    if (!pixData?.qr_code) return;
    await navigator.clipboard.writeText(pixData.qr_code);
    setCopied(true);
    toast.success("Código PIX copiado!");
    setTimeout(() => setCopied(false), 3000);
  }

  if (status === "approved") {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <CheckCircle2 className="w-16 h-16 text-primary" />
        <h3 className="text-xl font-bold text-primary">Pagamento aprovado!</h3>
        <p className="text-muted-foreground">Redirecionando...</p>
      </div>
    );
  }

  if (!pixData) {
    return (
      <div className="flex flex-col items-center gap-6 py-8">
        <QrCode className="w-16 h-16 text-muted-foreground" />
        <p className="text-muted-foreground text-center max-w-sm">
          Clique abaixo para gerar o QR Code PIX. O pagamento é instantâneo e confirmado automaticamente.
        </p>
        <Button onClick={createPixPayment} disabled={loading} size="lg">
          {loading ? (
            <>
              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
              Gerando PIX...
            </>
          ) : (
            "Gerar QR Code PIX"
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="text-center space-y-1">
        <h3 className="text-lg font-semibold">Escaneie o QR Code</h3>
        <p className="text-sm text-muted-foreground">
          Abra o app do seu banco e escaneie o código abaixo
        </p>
      </div>

      {pixData.qr_code_base64 && (
        <div className="bg-background p-4 rounded-xl shadow-md border">
          <img
            src={`data:image/png;base64,${pixData.qr_code_base64}`}
            alt="QR Code PIX"
            className="w-56 h-56"
          />
        </div>
      )}

      <div className="w-full max-w-sm space-y-3">
        <p className="text-sm font-medium text-center">Ou copie o código:</p>
        <div className="flex gap-2">
          <code className="flex-1 bg-muted px-3 py-2 rounded-md text-xs break-all max-h-20 overflow-auto">
            {pixData.qr_code}
          </code>
          <Button variant="outline" size="sm" onClick={copyCode}>
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Aguardando confirmação do pagamento...
      </div>
    </div>
  );
}
